import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  ArrowsClockwise, MicrosoftOutlookLogo, CheckCircle, WarningCircle,
  EnvelopeSimpleOpen, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Inbox() {
  const navigate = useNavigate();
  const [msStatus, setMsStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadStatus = () => api.get("/inbox/ms/status").then((r) => setMsStatus(r.data)).catch(() => {});
  const loadRecent = () => api.get("/tickets", { params: { limit: 25 } })
    .then((r) => setRecent(r.data.filter((t) => t.source === "email").slice(0, 10)));

  useEffect(() => { loadStatus(); loadRecent(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/inbox/ms/poll");
      const { created, appended, skipped, errors } = r.data;
      const bits = [];
      if (created) bits.push(`${created} new`);
      if (appended) bits.push(`${appended} replies`);
      if (skipped) bits.push(`${skipped} skipped`);
      if (errors) bits.push(`${errors} errors`);
      toast.success(bits.length ? `Synced: ${bits.join(" · ")}` : "No new emails");
      loadStatus();
      loadRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const stats = msStatus?.last_sync_stats;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Email Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">
            Emails to your shared mailbox become tickets automatically. Manage them in <button
              onClick={() => navigate("/tickets")}
              className="underline decoration-dotted hover:text-[#39A36E]"
              data-testid="inbox-go-to-tickets"
            >Tickets</button>.
          </p>
        </div>
        <Button
          data-testid="inbox-sync-now"
          onClick={syncNow}
          disabled={syncing || !msStatus?.configured}
          className="bg-[#39A36E] hover:bg-[#2E8C5D] h-9 gap-1"
        >
          <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      {/* Connection status card */}
      <div className="hairline rounded-lg p-5 bg-white">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#4f4c90]/10 border border-[#4f4c90]/25 flex items-center justify-center">
            <MicrosoftOutlookLogo size={22} weight="duotone" className="text-[#4f4c90]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-heading font-semibold text-lg">
                {msStatus?.mailbox || "Microsoft 365 shared inbox"}
              </div>
              {msStatus?.ok && (
                <span className="chip !text-[#39A36E] !border-[#39A36E]/40 bg-[#39A36E]/10 gap-1">
                  <CheckCircle size={11} weight="fill" /> Connected
                </span>
              )}
              {msStatus?.configured && msStatus?.ok === false && (
                <span className="chip !text-red-600 !border-red-500/30 bg-red-500/10 gap-1">
                  <WarningCircle size={11} weight="fill" /> Auth error
                </span>
              )}
              {!msStatus?.configured && (
                <span className="chip !text-slate-500 !border-slate-300 bg-slate-100 gap-1">
                  <WarningCircle size={11} /> Not configured
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Polling automatically every 2 minutes · Last sync {msStatus?.last_sync_at ? timeAgo(msStatus.last_sync_at) : "never"}
            </p>
          </div>
        </div>

        {stats && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">Scanned</div>
              <div className="text-2xl font-heading font-bold mt-1">{stats.scanned || 0}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-[#E9F5EE] p-3">
              <div className="text-[10px] uppercase text-[#2E8C5D] tracking-wider">New tickets</div>
              <div className="text-2xl font-heading font-bold mt-1 text-[#2E8C5D]">{stats.created || 0}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-[#EEF1F8] p-3">
              <div className="text-[10px] uppercase text-[#4f4c90] tracking-wider">Threaded replies</div>
              <div className="text-2xl font-heading font-bold mt-1 text-[#4f4c90]">{stats.appended || 0}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">Skipped</div>
              <div className="text-2xl font-heading font-bold mt-1">{stats.skipped || 0}</div>
            </div>
          </div>
        )}

        {msStatus?.configured && msStatus?.ok === false && msStatus?.error && (
          <div className="mt-3 text-[11px] font-mono text-red-700 bg-red-50 border border-red-200 rounded-md p-3 whitespace-pre-wrap break-all">
            {msStatus.error}
          </div>
        )}
      </div>

      {/* Recent email-created tickets */}
      <div className="hairline rounded-lg p-5 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-semibold">Recent tickets from email</div>
            <p className="text-xs text-slate-500 mt-1">The last few tickets created from inbound emails.</p>
          </div>
        </div>
        {recent.length === 0 ? (
          <div className="mt-4 p-6 text-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-md">
            <EnvelopeSimpleOpen size={22} className="mx-auto mb-2" />
            No email-created tickets yet. Once emails arrive, they'll show here.
          </div>
        ) : (
          <div className="mt-4 divide-y divide-slate-100">
            {recent.map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(`/tickets/${t.id}`)}
                className="w-full text-left py-3 px-1 flex items-start gap-3 row-hover rounded transition-colors"
                data-testid={`recent-email-${t.number}`}
              >
                <div className="w-8 h-8 rounded-full bg-[#39A36E]/10 flex items-center justify-center shrink-0">
                  <EnvelopeSimpleOpen size={14} weight="duotone" className="text-[#39A36E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500">
                    <span>#{t.number}</span>
                    <span>·</span>
                    <span>{timeAgo(t.created_at)}</span>
                    <span>·</span>
                    <span className="truncate">{t.requester_email}</span>
                  </div>
                  <div className="text-sm font-medium mt-0.5 truncate text-[#263E4F]">{t.subject}</div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <span className={`chip status-${t.status}`}>{t.status.replace("_", " ")}</span>
                    <span className={`chip prio-${t.priority}`}>{t.priority}</span>
                  </div>
                </div>
                <ArrowRight size={14} className="text-slate-400 mt-2 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
